import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { type KyRequestOptions } from '@vestigas/data';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { type CostCentersBulkResponse } from './types';
import { zodCostCentersBulkPayloadSchema } from './validations';

/**
 * Fetches multiple cost centers in bulk from the API.
 * @param {UUID[]} costCenterIds - Array of cost center IDs to fetch.
 * @returns {Promise<CostCenter[]>} Array of cost center data.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Accounting-Reference/operation/query_acc_ref_in_bulk_accounting_reference_query_bulk_post
 */
export const fetchCostCentersBulkAdmin = async (
  costCenterIds: UUID[],
  options?: KyRequestOptions,
) => {
  if (!costCenterIds?.length) {
    return [];
  }

  try {
    const response = await vestigasApi
      .post(ENDPOINT.COST_CENTER.GET_BULK_ADMIN(), {
        json: { ids: costCenterIds },
        validate: { body: zodCostCentersBulkPayloadSchema },
        ...options,
      })
      .json<CostCentersBulkResponse>();

    return response?.items ?? [];
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      // Re-throw AbortError without logging as it's expected when requests are cancelled
      throw error;
    }

    log.error({
      message: 'Error fetching Cost Centers Bulk Admin',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(($) => $.costCenter.getBulk.error422, {
            ns: 'data',
          });
          break;
        }

        case 429: {
          errorMessage = i18n.t(($) => $.costCenter.getBulk.error429, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(($) => $.costCenter.getBulk.error.generic, {
            ns: 'data',
          });
          break;
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
